import React, { useMemo } from "react";
import {
  Eye,
  Pencil,
  Trash2,
  MapPin,
  Phone,
  Landmark,
  Building2,
} from "lucide-react";

export default function OfficeTable({
  rows = [],
  variant = "table",
  query = "",
  onView = () => {},
  onEdit = () => {},
  onDelete = () => {},
}) {
  const list = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((o) => {
      const hay = `${o.name} ${o.address} ${o.city} ${o.state} ${o.country} ${
        o?.phone || ""
      } ${o?.radiusKm ?? ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, query]);

  if (variant === "grid")
    return (
      <GridView
        rows={list}
        onView={onView}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    );
  return (
    <TableView
      rows={list}
      onView={onView}
      onEdit={onEdit}
      onDelete={onDelete}
    />
  );
}

/* ---------------- GRID VIEW (Cards) ---------------- */

function GridView({ rows, onView, onEdit, onDelete }) {
  if (!rows?.length) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500 dark:border-white/10">
        No offices configured.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
      {rows.map((o) => (
        <article
          key={o.id}
          className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition hover:shadow-md dark:border-white/10 dark:bg-navy-800"
        >
          {/* Top banner */}
          <div className="flex h-24 items-center justify-center bg-gradient-to-b from-blue-50 to-blue-100/60 dark:from-white/5 dark:to-white/[0.03]">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white shadow-sm dark:bg-navy-700">
              <Building2 className="h-6 w-6 text-red-500 dark:text-blue-300" />
            </div>
          </div>

          {/* Content */}
          <div className="space-y-1 px-4 pb-3 pt-2">
            <h3 className="truncate text-base font-semibold text-navy-700 dark:text-white">
              {o.name}
            </h3>

            <div className="flex items-start gap-2 text-xs text-gray-600 dark:text-white/70">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
              <div className="line-clamp-2">
                {o.address}
                {o.city ? `, ${o.city}` : ""}
                {o.state ? `, ${o.state}` : ""}
                {o.country ? `, ${o.country}` : ""}
              </div>
            </div>

            {o.phone ? (
              <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-white/70">
                <Phone className="h-4 w-4 text-gray-400" />
                <span>{o.phone}</span>
              </div>
            ) : null}

            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-white/70">
              <Landmark className="h-4 w-4 text-gray-400" />
              <span>
                {safeFixed(o.lat, 4)}, {safeFixed(o.lng, 4)} • {o.radiusKm} km
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-3 py-2 dark:border-white/10">
            <IconBtn label="View" onClick={() => onView(o.id)}>
              <Eye className="h-4 w-4" />
            </IconBtn>
            <IconBtn label="Edit" onClick={() => onEdit(o.id)}>
              <Pencil className="h-4 w-4" />
            </IconBtn>
            <IconBtn
              label="Delete"
              tone="danger"
              onClick={() => onDelete(o.id)}
            >
              <Trash2 className="h-4 w-4" />
            </IconBtn>
          </div>
        </article>
      ))}
    </div>
  );
}

/* ---------------- TABLE VIEW ---------------- */

function TableView({ rows, onView, onEdit, onDelete }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-gray-200 dark:border-white/10">
      <table className="min-w-full text-left text-sm">
        <thead className="bg-gray-50 text-gray-600 dark:bg-white/5 dark:text-white/70">
          <tr>
            <th className="px-4 py-3">Office</th>
            <th className="px-4 py-3">Location</th>
            <th className="px-4 py-3">Coords</th>
            <th className="px-4 py-3">Geo-fence</th>
            <th className="px-4 py-3 text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          {!rows?.length ? (
            <tr>
              <td colSpan="5" className="px-4 py-6 text-center text-gray-500">
                No offices configured.
              </td>
            </tr>
          ) : (
            rows.map((o) => (
              <tr
                key={o.id}
                className="border-t border-gray-100 dark:border-white/10"
              >
                <td className="px-4 py-3">
                  <div className="font-medium text-navy-700 dark:text-white">
                    {o.name}
                  </div>
                  <div className="text-xs text-gray-500 ">{o.address}</div>
                </td>
                <td className="px-4 py-3 dark:text-white">
                  {[o.city, o.state, o.country].filter(Boolean).join(", ")}
                </td>
                <td className="px-4 py-3 dark:text-white">
                  {safeFixed(o.lat, 4)}, {safeFixed(o.lng, 4)}
                </td>
                <td className="px-4 py-3 dark:text-white">{o.radiusKm} km</td>
                <td className="px-4 py-3 text-center dark:text-white">
                  <div className="inline-flex items-center gap-1.5 ">
                    <IconBtn label="View" onClick={() => onView(o.id)}>
                      <Eye className="h-4 w-4" />
                    </IconBtn>
                    <IconBtn label="Edit" onClick={() => onEdit(o.id)}>
                      <Pencil className="h-4 w-4" />
                    </IconBtn>
                    <IconBtn
                      label="Delete"
                      onClick={() => onDelete(o.id)}
                    >
                      <Trash2 className="h-4 w-4 dark:text-white" />
                    </IconBtn>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

/* ---------------- little helpers ---------------- */

function IconBtn({ children, label, tone = "default", onClick }) {
  const base =
    "rounded-md p-2 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:hover:bg-white/10";
  const danger =
    "text-rose-600 hover:text-rose-700 dark:text-rose-400 dark:hover:text-rose-300";
  const normal = "text-gray-700 dark:text-white/80";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${base} ${tone === "danger" ? danger : normal} dark:text-white`}
      title={label}
      aria-label={label}
    >
      {children}
    </button>
  );
}

function safeFixed(n, d = 2) {
  const x = Number(n);
  return Number.isFinite(x) ? x.toFixed(d) : "-";
}
